import { IStorageHelperConfigurationOptions } from '../models/helpers/StorageHelper/IStorageHelperConfigurationOptions';
import { IStorageOption } from '../models/helpers/StorageHelper/IStorageOption';
import { IStorageOptions } from '../models/helpers/StorageHelper/IStorageOptions';
import { TStorageType } from '../models/helpers/StorageHelper/TStorageType';
import { jsonTryParse } from '../utils/json';
import { deObfuscate, isObfuscated, obfuscate } from '../utils/obfuscation';

export class StorageHelper {
	private type: TStorageType;
	private obfuscated?: boolean = false;
	private allowMultiple?: boolean = false;
	private getUserConfiguration?: (key: string) => Promise<object | null>;
	private setUserConfiguration?: (key: string, value: string) => Promise<boolean>;
	private removeUserConfiguration?: (key: string) => Promise<boolean>;

	constructor(storageHelperOptions: IStorageHelperConfigurationOptions) {
		this.type = storageHelperOptions.type;
		if (storageHelperOptions.obfuscated !== undefined) {
			this.obfuscated = storageHelperOptions.obfuscated;
		}
		if (storageHelperOptions.allowMultiple) {
			this.allowMultiple = storageHelperOptions.allowMultiple;
		}
		this.getUserConfiguration = storageHelperOptions.getUserConfiguration;
		this.setUserConfiguration = storageHelperOptions.setUserConfiguration;
		this.removeUserConfiguration = storageHelperOptions.removeUserConfiguration;
	}

	async getStorageValues(key: string): Promise<object | undefined> {
		if (this.type === 'DB') {
			return await this.getUserConfiguration(key);
		}
		// Get the local or session storage
		const options = this.getStorageOptions();
		let storageString = options?.target.storage.getItem(key);
		if (storageString) {
			if (isObfuscated(storageString)) {
				storageString = deObfuscate(storageString);
			}
			const storageConversion = jsonTryParse(storageString);
			if (storageConversion.isValid) {
				return Promise.resolve(storageConversion.value);
			}
		}
		return Promise.resolve(undefined);
	}

	async setStorageValues(key: string, value: any): Promise<boolean> {
		if (this.type === 'DB') {
			return this.setUserConfiguration(key, value);
		}
		let storageValue = JSON.stringify(value);
		if (this.obfuscated) {
			storageValue = obfuscate(storageValue);
		}
		// Get the local or session storage
		const options = this.getStorageOptions();
		if (options) {
			if (!this.allowMultiple) {
				options.clear.storage.removeItem(key);
			}
			options.target.storage.setItem(key, storageValue);
			return Promise.resolve(true);
		}
		return Promise.resolve(false);
	}

	async removeStorageValues(key: string): Promise<boolean> {
		if (this.type === 'DB') {
			return this.removeUserConfiguration(key);
		}
		const options = this.getStorageOptions();
		if (options) {
			options.target.storage.removeItem(key);
			options.clear.storage.removeItem(key);
			return Promise.resolve(true);
		}
		return Promise.resolve(false);
	}

	private getStorageOptions(): IStorageOptions | undefined {
		let options: IStorageOptions | undefined;
		// Have to access the storage from the window object, for the sake of the tests
		const localStorageOption: IStorageOption = {
			storage: window.localStorage,
		};
		const sessionStorageOption: IStorageOption = {
			storage: window.sessionStorage,
		};
		switch (this.type) {
			case 'LOCAL':
				options = {
					target: localStorageOption,
					clear: sessionStorageOption,
				};
				break;
			case 'SESSION':
				options = {
					target: sessionStorageOption,
					clear: localStorageOption,
				};
				break;
		}
		return options;
	}
}